<template>
  <div class="fee-discounts">
    <lenon-list-page
      title="Fee Discounts - Applied Discounts"
      :columns="columns"
      :rows="appliedDiscounts"
      :table-loading="tableLoading"
      :show-create="false"
      :show-search="true"
      search-placeholder="Search students with discounts"
      :show-profile-photo="false"
      @onRefresh="fetchAppliedDiscounts"
      :show-details="true"
    > 
      <template slot="right-extra-header-actions">
        <div
          v-responsive="$store.getters['app/largeScreen']"
          class="d-flex align-self-center align-items-start"
          style="margin-bottom: -20px;"
        >
          <lenon-button
            icon="PlusIcon"
            label="Create Discount"
            tool-tip-text="Create a new fee discount"
            class="mr-1"
            variant="outline-primary"
            @onClick="showCreateDiscountModal()"
          />
          <lenon-button
            icon="UserPlusIcon"
            label="Apply Discount"
            tool-tip-text="Apply discount to students"
            @onClick="showApplyDiscountModal()"
          />
        </div>
        <div
          v-responsive="$store.getters['app/smallScreen']"
          class="d-flex align-self-center align-items-start"
        >
          <lenon-dropdown left>
            <lenon-dropdown-item
              icon="PlusIcon"
              text="Create Discount"
              @click="showCreateDiscountModal()"
            />
            <lenon-dropdown-item
              icon="UserPlusIcon"
              text="Apply Discount"
              @click="showApplyDiscountModal()"
            />
          </lenon-dropdown>
        </div>
      </template>

      <template #row-details="{row}">
        <b-card>
          <b-row class="mb-2">
            <b-col md="6" class="mb-1">
              <strong>Fee Setting:</strong> {{ row.item.fee_discount.fee_setting.title }}
            </b-col>
            <b-col md="6" class="mb-1">
              <strong>Original Amount:</strong> {{ $store.getters['auth/currency'] }} {{ row.item.fee_discount.fee_setting.amount }}
            </b-col>
            <b-col md="6" class="mb-1">
              <strong>Discount Type:</strong> 
              <b-badge :variant="row.item.fee_discount.discount_type === 'percentage' ? 'warning' : 'light-warning'">
                {{ row.item.fee_discount.discount_type === 'percentage' ? 'Percentage' : 'Fixed Amount' }}
              </b-badge>
            </b-col>
            <b-col md="6" class="mb-1">
              <strong>Discount Value:</strong> 
              {{ row.item.fee_discount.discount_type === 'percentage' ? row.item.fee_discount.discount + '%' : $store.getters['auth/currency'] + ' ' + row.item.fee_discount.discount }}
            </b-col>
            <b-col md="6" class="mb-1">
              <strong>Discounted Amount:</strong> 
              {{ $store.getters['auth/currency'] + ' ' + calculatedAmount(row) }}
            </b-col>
            <b-col md="12" class="mb-1">
              <strong>Applied Date:</strong> {{ formatDate(row.item.created_at) }}
            </b-col>
          </b-row>
        </b-card>
      </template>

      <template #index="{row}">
        {{ row.index + 1 }}
      </template>

      <template #student_name="{row}">
        <div class="d-flex align-items-center">
          <b-avatar
            v-if="row.item.student.photo"
            :src="`${$store.getters['auth/storageUrl']}/${row.item.student.photo}`"
            size="32"
            class="mr-1"
          />
          <b-avatar
            v-else
            :text="row.item.student.first_name.charAt(0) + row.item.student.last_name.charAt(0)"
            size="32"
            variant="light-primary"
            class="mr-1"
          />
          <div>
            <div class="font-weight-bolder">
              {{ row.item.student.first_name }} {{ row.item.student.last_name }}
            </div>
            <small class="text-gray">{{ row.item.student.student_id }}</small>
          </div>
        </div>
      </template>

      <template #class_name="{row}">
        <b-badge variant="light-primary">
          {{ row.item.student.class_name || 'N/A' }}
        </b-badge>
      </template>

      <template #discount_type="{row}">
        <b-badge :variant="row.item.fee_discount.discount_type === 'percentage' ? 'warning' : 'light-warning'">
          {{ row.item.fee_discount.discount_type === 'percentage' ? 'Percentage' : 'Fixed Amount' }}
        </b-badge>
      </template>

      <template #discount_value="{row}">
        <strong>
          {{ row.item.fee_discount.discount_type === 'percentage' ? row.item.fee_discount.discount + '%' : $store.getters['auth/currency'] + ' ' + row.item.fee_discount.discount }}
        </strong>
      </template>

      <template #actions="{row}">
        <lenon-button
            icon-only
            :variant="'flat-primary'"
            :icon="row.detailsShowing?'EyeOffIcon':'EyeIcon'"
            @onClick="row.toggleDetails"
        />
        <lenon-button
          icon-only
          variant="flat-danger"
          icon="TrashIcon"
          tool-tip-text="Remove Discount"
          @onClick="confirmRemoveDiscount(row.item.id)"
        />
      </template>
    </lenon-list-page>

    <!-- Create Discount Modal -->
    <fee-discount-setup
      :modal-opened="createDiscountModalOpened"
      @modalClosed="closeModal('createDiscount')"
      @discountCreated="handleDiscountCreated"
    />

    <!-- Apply Discount Modal -->
    <applied-discount-setup
      :modal-opened="applyDiscountModalOpened"
      @modalClosed="closeModal('applyDiscount')"
      @discountApplied="handleDiscountApplied"
    />
  </div>
</template>

<script>
import LenonButton from '@/lenon/components/LenonButton.vue'
import showToast from '@/lenon/mixins/showToast'
import confirm from '@/lenon/mixins/confirm'
import FeeDiscountSetup from '@/views/finance/fee-discounts/FeeDiscount.vue'
import AppliedDiscountSetup from '@/views/finance/fee-discounts/AppliedDiscount.vue'
import LenonListPage from '@/lenon/components/LenonListPage.vue'
import {
  BBadge, BCard, BCol, BRow, BAvatar,
} from 'bootstrap-vue'
import logData from '@/libs/log'
import LenonDropdown from '@/lenon/components/LenonDropdown'
import LenonDropdownItem from '@/lenon/components/LenonDropdownItem'

export default {
  name: 'FeeDiscountsIndex',
  components: {
    LenonDropdownItem,
    LenonDropdown,
    LenonListPage,
    FeeDiscountSetup,
    AppliedDiscountSetup,
    LenonButton,
    BCard,
    BRow,
    BCol,
    BBadge,
    BAvatar,
  },
  mixins: [showToast, confirm],
  data() {
    return {
      createDiscountModalOpened: false,
      applyDiscountModalOpened: false,
      tableLoading: false,
      columns: [
        {
          key: 'index',
          label: '#',
        },
        {
          key: 'student_name',
          label: 'Student',
        },
        {
          key: 'class_name',
          label: 'Class',
        },
        {
          key: 'fee_discount.title',
          label: 'Discount Title',
        },
        {
          key: 'fee_discount.fee_setting.title',
          label: 'Fee Setting',
        },
        {
          key: 'discount_type',
          label: 'Discount Type',
        },
        {
          key: 'discount_value',
          label: 'Discount Value',
        },
        {
          key: 'actions',
          label: 'Actions',
        },
      ],
    }
  },
  computed: {
    appliedDiscounts() {
      return this.$store.getters['feeDiscounts/appliedDiscounts']
    },
  },
  mounted() {
    this.fetchAppliedDiscounts()
    this.fetchFeeDiscounts()
  },
  methods: {
    calculatedAmount(row) {
      if (!row.item.fee_discount.fee_setting || !row.item.fee_discount.discount || !row.item.fee_discount.discount_type) {
        return row.item.fee_discount.fee_setting ? row.item.fee_discount.fee_setting.amount : 0
      }

      let amount = row.item.fee_discount.fee_setting.amount
      if (row.item.fee_discount.discount_type === 'flat') {
        amount = amount - parseFloat(row.item.fee_discount.discount)
      } else if (row.item.fee_discount.discount_type === 'percentage') {
        amount = amount - (parseFloat(row.item.fee_discount.discount) / 100 * amount)
      }
      return Math.max(0, amount).toFixed(2)
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    closeModal(type) {
      switch (type) {
        case 'createDiscount':
          this.createDiscountModalOpened = false
          break
        case 'applyDiscount':
          this.applyDiscountModalOpened = false
          break
      }
    },
    showCreateDiscountModal() {
      this.createDiscountModalOpened = true
    },
    showApplyDiscountModal() {
      this.applyDiscountModalOpened = true
    },
    handleDiscountCreated() {
      this.fetchAppliedDiscounts()
      this.showSuccess('Discount created successfully')
    },
    handleDiscountApplied() {
      this.fetchAppliedDiscounts()
    },
    async confirmRemoveDiscount(id) {
      const confirmed = await this.confirmAction(
        'Are you sure you want to remove this discount? This action cannot be undone.',
        'Yes, Remove',
        'Cancel',
        'danger'
      )
      if (confirmed) {
        this.removeAppliedDiscount(id)
      }
    },
    removeAppliedDiscount(id) {
      this.tableLoading = true
      this.$http.delete(`applied-discounts/${id}`)
        .then(() => {
          this.$store.commit('feeDiscounts/removeAppliedDiscount', id)
          this.showSuccess('Applied discount removed successfully')
        })
        .catch(err => {
          logData(err)
          this.showError('Failed to remove applied discount')
        })
        .finally(() => {
          this.tableLoading = false
        })
    },
    fetchAppliedDiscounts() {
      this.tableLoading = this.appliedDiscounts.length < 1
      this.$http.get('applied-discounts')
        .then(res => {
          this.$store.commit('feeDiscounts/setAppliedDiscounts', res.data)
        })
        .catch(err => {
          logData(err)
          this.showError('Failed to load applied discounts')
        })
        .finally(() => {
          this.tableLoading = false
        })
    },
    fetchFeeDiscounts() {
      this.tableLoading = this.appliedDiscounts.length < 1
      this.$http.get('fee-discounts')
        .then(res => {
          this.$store.commit('feeDiscounts/setFeeDiscounts', res.data)
        })
        .catch(err => {
          logData(err)
          this.showError('Failed to load fee discounts')
        })
        .finally(() => {
          this.tableLoading = false
        })
    },
  },
}
</script>
